import type * as Y from "yjs";
import { newId } from "./blocks";

export const REVIEWS_KEY = "reviews";

export type ReviewStatus = "pending" | "approved" | "changes_requested";
export type ReviewLinkType = "url" | "text" | "none";

export type Review = {
  id: string;
  /** The person whose assistant asked — only they see and answer it. */
  forUserId: string;
  forUserName: string;
  title: string;
  summary: string;
  link: string;
  linkType: ReviewLinkType;
  at: number;
  status: ReviewStatus;
  feedback: string;
  answeredAt: number | null;
};

/** Only http(s) links become a button; anything else is shown as plain text. */
export function isUrlLink(link: string): boolean {
  return /^https?:\/\/\S+$/i.test(link.trim());
}

export function reviewsMap(doc: Y.Doc): Y.Map<Review> {
  return doc.getMap<Review>(REVIEWS_KEY);
}

export function createReview(
  doc: Y.Doc,
  input: {
    userId: string;
    userName: string;
    title: string;
    summary: string;
    link?: string;
    at?: number;
  },
): Review {
  const link = input.link?.trim() ?? "";
  const review: Review = {
    id: newId("rv"),
    forUserId: input.userId,
    forUserName: input.userName,
    title: input.title,
    summary: input.summary,
    link,
    linkType: link === "" ? "none" : isUrlLink(link) ? "url" : "text",
    at: input.at ?? Date.now(),
    status: "pending",
    feedback: "",
    answeredAt: null,
  };
  reviewsMap(doc).set(review.id, review);
  return review;
}

export function readReview(doc: Y.Doc, reviewId: string): Review | null {
  return reviewsMap(doc).get(reviewId) ?? null;
}

export function readReviews(doc: Y.Doc): Review[] {
  return [...reviewsMap(doc).values()];
}

export function pendingReviewsFor(doc: Y.Doc, userId: string): Review[] {
  return readReviews(doc)
    .filter((review) => review.forUserId === userId && review.status === "pending")
    .sort((a, b) => a.at - b.at);
}

export function answerReview(
  doc: Y.Doc,
  reviewId: string,
  answer: { status: Exclude<ReviewStatus, "pending">; feedback?: string; at?: number },
): void {
  const review = readReview(doc, reviewId);
  // Whoever answers first decides; a second answer must not overwrite it.
  if (!review || review.status !== "pending") return;
  reviewsMap(doc).set(reviewId, {
    ...review,
    status: answer.status,
    feedback: answer.feedback ?? "",
    answeredAt: answer.at ?? Date.now(),
  });
}

/** Keeps the board tidy: answered reviews older than the cutoff are removed. */
export function pruneAnsweredReviews(doc: Y.Doc, olderThan: number): void {
  doc.transact(() => {
    for (const review of readReviews(doc)) {
      if (review.status !== "pending" && (review.answeredAt ?? 0) < olderThan) {
        reviewsMap(doc).delete(review.id);
      }
    }
  });
}
